import React, { useState, useEffect } from 'react';
import { fetchSubCategories } from '../../../../api/masters/styles/subCategory/subCategoryApi';
import dayjs from 'dayjs';
import { fetchCompanies } from '../../../../api/masters/company/companyApi';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Papa from 'papaparse';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Alert from '@mui/material/Alert';
import { useSelector } from 'react-redux';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { importSubCategoryCSV } from '../../../../api/masters/styles/subCategory/subCategoryApi';

const SubCategoryForm = ({ onSubmit, editingSubCategory, onCancel, formSuccess = '', formError = '', setFormSuccess, setFormError, categories = [], users = [] }) => {
  const [form, setForm] = useState({ company_code: '', sub_category_code: '', sub_category_name: '', category_code: '', status: 'Active' });
  const [companies, setCompanies] = useState([]);
  const [focus, setFocus] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeExists, setCodeExists] = useState(false);
  const subCategoryList = useSelector(state => state.subCategory.list || []);
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');
  const [importErrors, setImportErrors] = useState([]);
  // Add state for import dialog and messages
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importedRows, setImportedRows] = useState([]);
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    async function loadCompanies() {
      const data = await fetchCompanies();
      setCompanies(data.filter(c => c.trash === 0));
    }
    loadCompanies();
  }, []);

  useEffect(() => {
    if (editingSubCategory) {
      setForm({
        company_code: editingSubCategory.company_code || '',
        ...editingSubCategory,
        category_code: editingSubCategory.category_code || '',
      });
    } else {
      setForm({ company_code: '', sub_category_code: '', sub_category_name: '', category_code: '', status: 'Active' });
    }
    setErrors({});
    setCodeExists(false);
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  }, [editingSubCategory]);

  useEffect(() => {
    if (formSuccess) {
      const timer = setTimeout(() => {
        setFormSuccess && setFormSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [formSuccess, setFormSuccess]);

  useEffect(() => {
    if (importSuccess || importError) {
      const timer = setTimeout(() => {
        setImportSuccess('');
        setImportError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [importSuccess, importError]);

  const checkCodeExists = async (code) => {
    setCodeChecking(true);
    try {
      const subCategories = await fetchSubCategories();
      const exists = subCategories.some(
        (c) => c.sub_category_code === code && (!editingSubCategory || c.sub_category_id !== editingSubCategory.sub_category_id)
      );
      setCodeExists(exists);
      if (exists) {
        setErrors((prev) => ({ ...prev, sub_category_code: 'This code already exists and cannot be reused.' }));
      } else {
        setErrors((prev) => ({ ...prev, sub_category_code: undefined }));
      }
      return exists;
    } catch (e) {
      setCodeExists(false);
      setErrors((prev) => ({ ...prev, sub_category_code: undefined }));
      return false;
    } finally {
      setCodeChecking(false);
    }
  };

  // Helper function for alphanumeric check
  const isAlphanumeric = (str) => /^[A-Za-z0-9]+$/.test(str);

  const validate = async () => {
    const newErrors = {};
    const code = form.sub_category_code.trim();
    if (!form.company_code) newErrors.company_code = 'Please select a company code.';
    if (!code) newErrors.sub_category_code = 'Please enter a code.';
    else if (/[^A-Za-z0-9]/.test(code)) newErrors.sub_category_code = 'Code can only contain letters and numbers.';
    if (!form.sub_category_name.trim()) newErrors.sub_category_name = 'Please enter a sub-category name.';
    if (!form.category_code) newErrors.category_code = 'Please select a category.';
    if (code && isAlphanumeric(code)) {
      const exists = await checkCodeExists(code);
      if (exists) newErrors.sub_category_code = 'This code already exists and cannot be reused.';
    }
    setErrors(newErrors);
    if (setFormError) setFormError('');
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    if (name === 'sub_category_code') {
      if (value && /[^A-Za-z0-9]/.test(value)) {
        setErrors(prev => ({ ...prev, [name]: 'Code can only contain letters and numbers.' }));
      } else {
        setErrors(prev => ({ ...prev, [name]: undefined }));
      }
    } else {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }

    setCodeExists(false);
    if (setFormError) setFormError('');
    if (setFormSuccess) setFormSuccess('');
  };

  const handleFocus = e => setFocus({ ...focus, [e.target.name]: true });

  const handleBlur = async (e) => {
    setFocus({ ...focus, [e.target.name]: false });
    if (e.target.name === 'sub_category_code') {
      const value = form.sub_category_code.trim();
      if (!value) {
        setErrors((prev) => ({ ...prev, sub_category_code: 'Please enter a code.' }));
        return;
      }
      if (/[^A-Za-z0-9]/.test(value)) {
        setErrors((prev) => ({ ...prev, sub_category_code: 'Code can only contain letters and numbers.' }));
        return;
      }
      await checkCodeExists(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isValid = await validate();
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }
    setErrors({});
    try {
      await onSubmit(form);
      if (setFormSuccess)
        setFormSuccess(editingSubCategory ? 'Sub-Category updated successfully!' : 'Sub-Category added successfully!');
      if (setFormError) setFormError('');
      if (!editingSubCategory) {
        setForm({ company_code: '', sub_category_code: '', sub_category_name: '', category_code: '', status: 'Active' });
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      if (
        err?.response?.status === 409 ||
        (message && message.toLowerCase().includes('already exists'))
      ) {
        setErrors((prev) => ({
          ...prev,
          sub_category_code: 'This code already exists and cannot be reused.',
        }));
        if (setFormSuccess) setFormSuccess('');
        if (setFormError) setFormError('');
        return;
      }
      if (setFormError) setFormError(message || 'An error occurred.');
      if (setFormSuccess) setFormSuccess('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setForm({ company_code: '', sub_category_code: '', sub_category_name: '', category_code: '', status: 'Active' });
    setErrors({});
    setCodeExists(false);
    if (setFormSuccess) setFormSuccess('');
    if (setFormError) setFormError('');
  };

  // Download Template logic
  const handleDownloadTemplate = () => {
    const headers = ['sub_category_code','sub_category_name','category_name','status','company_code'];
    const csv = Papa.unparse([headers]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', 'sub_category_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Import CSV logic
  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const required = ['sub_category_code','sub_category_name','category_name','status','company_code'];
        const missing = required.filter(f => !results.meta.fields.includes(f));
        if (missing.length) {
          setImportError('Missing required fields: ' + missing.join(', '));
          setImportDialogOpen(true);
          return;
        }
        setImportedRows(results.data);
        setImportFile(file);
        setImportDialogOpen(true);
        setImportError('');
      },
      error: (err) => {
        setImportError('CSV parse error: ' + err.message);
        setImportDialogOpen(true);
      }
    });
  };
  const handleConfirmImport = async () => {
    if (!importFile) return;
    setImportSuccess('');
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await importSubCategoryCSV(formData);
      setImportSuccess(`Imported: ${res.imported}, Failed: ${res.failed}`);
      setImportErrors(res.errors || []);
    } catch (err) {
      setImportError(err?.response?.data?.error || err.message);
    }
  };

  // Helper to format date
  const formatDate = (date) => date ? dayjs(date).format('DD-MM-YYYY hh:mm A') : 'Unknown';
  // Helper to get user display name and role (if users list is available)
  const getUserDisplay = (userId) => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.user_id === userId);
    if (!user) return 'Unknown';
    return `${user.username} (${user.role_name || 'Unknown'})`;
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', p: 0 }}>
      {/* Download Template and Import CSV Buttons - compact, green, inline */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: 'flex-start' }}>
        <Button
          variant="contained"
          startIcon={<CloudDownloadIcon />}
          onClick={handleDownloadTemplate}
          sx={{
            bgcolor: '#8e24aa',
            '&:hover': { bgcolor: '#6a1b9a' },
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            minWidth: 110,
            height: 36,
            fontSize: 12,
            letterSpacing: 1,
            boxShadow: 'none',
            px: 1.5,
          }}
        >
          Download Template
        </Button>
        <Button
          variant="contained"
          component="label"
          startIcon={<UploadFileIcon />}
          sx={{
            bgcolor: '#00bcd4',
            '&:hover': { bgcolor: '#008ba3' },
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 2,
            minWidth: 110,
            height: 36,
            fontSize: 12,
            letterSpacing: 1,
            boxShadow: 'none',
            px: 1.5,
          }}
        >
          Import CSV
          <input
            type="file"
            accept=".csv"
            hidden
            onChange={handleImportCSV}
          />
        </Button>
      </Stack>
      {importSuccess && <Alert severity="success" sx={{ mb: 2 }}>{importSuccess}</Alert>}
      {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
      {importErrors && importErrors.length > 0 && (
        <Box sx={{ color: 'red', fontSize: 13, mt: 1, mb: 2 }}>
          {importErrors.map((err, idx) => (
            <div key={idx} style={{ marginBottom: 6 }}>
              <b>Row:</b> {JSON.stringify(err.row)}<br />
              <b>Error:</b> {err.error}
            </div>
          ))}
        </Box>
      )}
      <Box sx={{ fontWeight: 700, fontSize: 18, color: '#111', mb: 2.5, letterSpacing: 0.7, textAlign: 'left', width: '100%' }}>
        {editingSubCategory ? 'Edit Sub-Category' : 'Add Sub-Category'}
      </Box>
      {formError && <Box sx={{ color: 'red', fontSize: 15, mb: 1 }}>{formError}</Box>}
      {formSuccess && !Object.values(errors).some(Boolean) && (
        <Box sx={{ color: '#00C853', fontSize: 15, fontWeight: 500, mb: 1 }}>{formSuccess}</Box>
      )}
      <Stack spacing={2} alignItems="center">
        <FormControl fullWidth error={!!errors.company_code} size="small">
            {/* Autocomplete for company: search by code or name, label as CMP001 - Mindtree */}
            <Autocomplete
              id="company_code"
              options={companies}
              getOptionLabel={(option) => option.company_code && option.company_name ? `${option.company_code} - ${option.company_name}` : ''}
              value={companies.find(c => c.company_code === form.company_code) || null}
              onChange={(event, newValue) => {
                setForm({ ...form, company_code: newValue ? newValue.company_code : '' });
                setErrors(prev => ({ ...prev, company_code: undefined }));
                if (setFormError) setFormError('');
                if (setFormSuccess) setFormSuccess('');
              }}
              isOptionEqualToValue={(option, value) => option.company_code === value.company_code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company Code"
                  error={!!errors.company_code}
                  helperText={errors.company_code}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: 45,
                      height: 45,
                      color: '#111',
                      '& fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#BDBDBD' },
                      '&:hover fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#BDBDBD' },
                      '&.Mui-focused fieldset': { borderColor: !!errors.company_code ? '#D32F2F' : '#00C853' },
                    },
                    '& label.Mui-focused': { color: '#00C853' },
                  }}
                  inputProps={{ ...params.inputProps, maxLength: 50 }}
                />
              )}
              filterOptions={(options, { inputValue }) =>
                options.filter(option => {
                  const search = inputValue.toLowerCase();
                  return (
                    (option.company_name && option.company_name.toLowerCase().includes(search)) ||
                    (option.company_code && option.company_code.toLowerCase().includes(search))
                  );
                })
              }
              clearOnEscape
            />
          </FormControl>
          <TextField
            label="SubCategory Code*"
            name="sub_category_code"
            value={form.sub_category_code}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.sub_category_code}
            helperText={errors.sub_category_code}
            fullWidth
            inputProps={{ maxLength: 20 }}
            disabled={!!editingSubCategory || codeChecking}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#111',
                minHeight: 45,
                height: 45,
                '& fieldset': { borderColor: !!errors.sub_category_code ? '#D32F2F' : '#BDBDBD' },
                '&:hover fieldset': { borderColor: !!errors.sub_category_code ? '#D32F2F' : '#BDBDBD' },
                '&.Mui-focused fieldset': { borderColor: !!errors.sub_category_code ? '#D32F2F' : '#00C853' },
                background: '#fff',
              },
              mb: 1.5,
              '& label.Mui-focused': { color: '#00C853' },
            }}
          />
          {codeChecking && <Box sx={{ color: '#00C853', fontSize: 12 }}>Checking...</Box>}
          <TextField
            label="SubCategory Name*"
            name="sub_category_name"
            value={form.sub_category_name}
            onChange={handleChange}
            error={!!errors.sub_category_name}
            helperText={errors.sub_category_name}
            fullWidth
            inputProps={{ maxLength: 50 }}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#111',
                minHeight: 45,
                height: 45,
                '& fieldset': { borderColor: !!errors.sub_category_name ? '#D32F2F' : '#BDBDBD' },
                '&:hover fieldset': { borderColor: !!errors.sub_category_name ? '#D32F2F' : '#BDBDBD' },
                '&.Mui-focused fieldset': { borderColor: !!errors.sub_category_name ? '#D32F2F' : '#00C853' },
                background: '#fff',
              },
              mb: 1.5,
              '& label.Mui-focused': { color: '#00C853' },
            }}
          />
          <FormControl fullWidth error={!!errors.category_code} size="small">
            {/* Autocomplete for category: search by name only */}
            <Autocomplete
              id="category_code"
              options={categories}
              getOptionLabel={(option) => option.category_name || ''}
              value={categories.find(c => c.category_code === form.category_code) || null}
              onChange={(event, newValue) => {
                setForm({ ...form, category_code: newValue ? newValue.category_code : '' });
                setErrors(prev => ({ ...prev, category_code: undefined }));
                if (setFormError) setFormError('');
                if (setFormSuccess) setFormSuccess('');
              }}
              isOptionEqualToValue={(option, value) => option.category_code === value.category_code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Category"
                  error={!!errors.category_code}
                  helperText={errors.category_code}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      minHeight: 45,
                      height: 45,
                      color: '#111',
                      '& fieldset': { borderColor: !!errors.category_code ? '#D32F2F' : '#BDBDBD' },
                      '&:hover fieldset': { borderColor: !!errors.category_code ? '#D32F2F' : '#BDBDBD' },
                      '&.Mui-focused fieldset': { borderColor: !!errors.category_code ? '#D32F2F' : '#00C853' },
                    },
                    '& label.Mui-focused': { color: '#00C853' },
                  }}
                  inputProps={{ ...params.inputProps, maxLength: 50 }}
                />
              )}
              filterOptions={(options, { inputValue }) =>
                options.filter(option =>
                  option.category_name &&
                  option.category_name.toLowerCase().includes(inputValue.toLowerCase())
                )
              }
              clearOnEscape
            />
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="status-label" sx={{ color: '#111', '&.Mui-focused': { color: '#00C853' } }}>Status</InputLabel>
            <Select
              labelId="status-label"
              id="status"
              name="status"
              value={form.status}
              label="Status"
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={{
                minHeight: 45,
                height: 45,
                '& .MuiOutlinedInput-root': {
                  minHeight: 45,
                  height: 45,
                  color: '#111',
                  background: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00C853 !important' },
                },
              }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          {(editingSubCategory || form.created_by || form.created_at) && (
            <Box sx={{ mt: 2, mb: 1, background: '#f8f9f6', borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', p: 2, color: '#222', fontSize: 14, width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ color: '#4caf50', marginRight: 6 }}>●</span>
                <span style={{ fontWeight: 600, marginRight: 6 }}>Created by:</span>
                <span style={{ marginRight: 12 }}>{getUserDisplay(editingSubCategory?.created_by || form.created_by)}</span>
                <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}>⏰</span>
                <span>{formatDate(editingSubCategory?.created_at || form.created_at)}</span>
              </div>
              {editingSubCategory && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ color: '#ff9800', marginRight: 6 }}>●</span>
                  <span style={{ fontWeight: 600, marginRight: 6 }}>Last updated by:</span>
                  <span style={{ marginRight: 12 }}>{getUserDisplay(editingSubCategory.updated_by)}</span>
                  <span style={{ color: '#2196f3', fontSize: 13, marginRight: 3 }}>⏰</span>
                  <span>{formatDate(editingSubCategory.updated_at)}</span>
                </div>
              )}
            </Box>
          )}
          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <Button type="submit" fullWidth variant="contained" sx={{ bgcolor: '#00C853', '&:hover': { bgcolor: '#009e46' }, minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1 }} disabled={isSubmitting || codeExists}>
              {editingSubCategory ? 'Update' : 'Add'}
            </Button>
            <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={handleClear} disabled={isSubmitting}>
              Clear
            </Button>
            {editingSubCategory && (
              <Button type="button" fullWidth variant="outlined" sx={{ color: '#00C853', borderColor: '#00C853', minWidth: 90, height: 40, fontWeight: 700, fontSize: 16, letterSpacing: 1, '&:hover, &:active': { borderColor: '#009e46', color: '#009e46' } }} onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
          </Stack>
        </Stack>
      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Sub-Categories</DialogTitle>
        <DialogContent>
          {importError && <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>}
          {importSuccess && <Alert severity="success" sx={{ mb: 2 }}>{importSuccess}</Alert>}
          {importedRows.length > 0 && (
            <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>sub_category_code</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>sub_category_name</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>category_name</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>status</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, fontSize: 16, borderBottom: '2px solid #e0e0e0', background: '#f8f9fa' }}>company_code</th>
                  </tr>
                </thead>
                <tbody>
                  {importedRows.map((row, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.sub_category_code}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.sub_category_name}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.category_name}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.status}</td>
                      <td style={{ textAlign: 'left', padding: '8px 12px', fontSize: 15, color: '#222' }}>{row.company_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
          {importErrors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">Some rows failed to import:</Alert>
              <ul>
                {importErrors.map((err, idx) => (
                  <li key={idx}>{err.error} (Row: {JSON.stringify(err.row)})</li>
                ))}
              </ul>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            onClick={() => setImportDialogOpen(false)}
            variant="contained"
            sx={{
              bgcolor: '#757575',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 2,
              minWidth: 110,
              height: 38,
              fontSize: 12,
              letterSpacing: 1,
              boxShadow: 'none',
              mr: 1.5,
              '&:hover': { bgcolor: '#424242' },
            }}
          >
            Cancel
          </Button>
          {!importSuccess && !importError && importedRows.length > 0 && (
            <Button
              onClick={handleConfirmImport}
              variant="contained"
              sx={{
                bgcolor: '#43a047',
                color: '#fff',
                fontWeight: 700,
                borderRadius: 2,
                minWidth: 110,
                height: 38,
                fontSize: 12,
                letterSpacing: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#2e7d32' },
              }}
            >
              Confirm Import
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SubCategoryForm;